import { PHOTO_LABEL_NAME_MAX_LENGTH } from "@rebuild/shared";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useEffect, useState } from "react";

type AddPhotoLabelDialogProps = {
  open: boolean;
  existingNames: string[];
  isSaving: boolean;
  onCancel: () => void;
  onConfirm: (name: string) => void | Promise<void>;
};

export function AddPhotoLabelDialog({ open, existingNames, isSaving, onCancel, onConfirm }: AddPhotoLabelDialogProps) {
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName("");
      setError(null);
    }
  }, [open]);

  const trimmedName = name.trim();
  const isDuplicate = existingNames.some((n) => n.trim().toLowerCase() === trimmedName.toLowerCase());
  const canSubmit = trimmedName.length > 0 && !isDuplicate && !isSaving;

  const handleSubmit = async () => {
    if (trimmedName.length === 0) {
      setError("Label name is required.");
      return;
    }
    if (isDuplicate) {
      setError("A label with this name already exists.");
      return;
    }
    setError(null);
    await onConfirm(trimmedName);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? null : onCancel())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add photo label</DialogTitle>
          <DialogDescription>Your team will be able to pick this label when labelling photos.</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="add-photo-label-name" className="text-foreground text-sm font-medium">
              Label name
            </label>
            <Input
              id="add-photo-label-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (error) setError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void handleSubmit();
                }
              }}
              maxLength={PHOTO_LABEL_NAME_MAX_LENGTH}
              placeholder="e.g. Moisture reading"
              autoComplete="off"
              autoFocus
            />
          </div>

          {open && isDuplicate && trimmedName.length > 0 ? (
            <p className="text-destructive text-sm">A label with this name already exists.</p>
          ) : error ? (
            <p className="text-destructive text-sm">{error}</p>
          ) : null}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="button" onClick={() => void handleSubmit()} disabled={!canSubmit}>
            {isSaving ? "Adding…" : "Add label"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
